import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ProductName } from 'situview-webapi-client';
import { Utils } from './portal-common';

@Injectable({
  providedIn: 'root'
})
export class ProductNameService {

  private readonly productNameConfigurationSubject = new BehaviorSubject<IProductNameConfiguration>({} as IProductNameConfiguration);
  readonly productNameConfiguration$ = this.productNameConfigurationSubject.asObservable();

  configurations: IProductNameConfiguration[] | undefined;
  colorPool: string[] = [];
  baseIngredients: IBaseIngredient[] = []

  constructor() {
    this.colorPool = Utils.getColorPool().reverse();
    this.configurations = [
      { productName: ProductName.Panorama, product: "Standard Panorama", productColor: this.colorPool[0] },
      { productName: ProductName.RayTraced, product: "Photo Realistic Panorama", productColor: this.colorPool[1] },
      { productName: ProductName.RayTracedPlus, product: "Photo Realistic + Panorama", productColor: this.colorPool[2] },
      { productName: ProductName.VR, product: "Virtual Reality", productColor: this.colorPool[3] },
      { productName: ProductName.Render, product: "situRENDER", productColor: this.colorPool[4] },
      { productName: ProductName.Facade, product: "situFACADE", productColor: this.colorPool[5] },
      { productName: ProductName.Plan, product: "situPLAN", productColor: this.colorPool[6] },
    ];
    this.productNameConfiguration = this.configurations[0];
    this.baseIngredients = [
      {
        artifactKeyName: 'ifc', displayName: 'IFC Files', callerReferenceKey: '', isCutOffDate: true,
        products: [ProductName.Facade, ProductName.Insights, ProductName.Panorama, ProductName.Plan, ProductName.RayTraced, ProductName.Render, ProductName.VR, ProductName.Vanity, ProductName.Unreal]
      },
      {
        artifactKeyName: 'builderifcsettings', displayName: 'Builder Ifc Settings', callerReferenceKey: '', isCutOffDate: false,
        products: [ProductName.Facade, ProductName.Insights, ProductName.Panorama, ProductName.Plan, ProductName.RayTraced, ProductName.Render, ProductName.VR, ProductName.Vanity, ProductName.Unreal]
      },
      {
        artifactKeyName: 'software', displayName: 'Bim Viewer', callerReferenceKey: 'software\\bimviewer', isCutOffDate: false,
        products: [ProductName.Facade, ProductName.Insights, ProductName.Panorama, ProductName.Plan, ProductName.RayTraced, ProductName.Render, ProductName.VR, ProductName.Unreal]
      },
      {
        artifactKeyName: 'software', displayName: 'Floor Plan Tool', callerReferenceKey: 'software\\floorplantool', isCutOffDate: false,
        products: [ProductName.Plan]
      },
      {
        artifactKeyName: 'software', displayName: 'IFC 4 To JSON', callerReferenceKey: 'software\\ifc4tojson', isCutOffDate: false,
        products: [ProductName.Facade, ProductName.Insights, ProductName.Panorama, ProductName.Plan, ProductName.RayTraced, ProductName.Render, ProductName.VR]
      },
      {
        artifactKeyName: 'software', displayName: 'Lightmap And Bundle', callerReferenceKey: 'software\\lightmapandbundle', isCutOffDate: false,
        products: [ProductName.VR]
      },
      {
        artifactKeyName: 'software', displayName: 'Pathtrace', callerReferenceKey: 'software\\pathtrace', isCutOffDate: false,
        products: [ProductName.Facade, ProductName.RayTraced, ProductName.Render]
      },
      {
        artifactKeyName: 'software', displayName: 'SituPlan Bundle', callerReferenceKey: 'software\\situplanbundle', isCutOffDate: false,
        products: [ProductName.Plan]
      },
      {
        artifactKeyName: 'software', displayName: 'Vanity Generator', callerReferenceKey: 'software\\vanitygenerator', isCutOffDate: false,
        products: [ProductName.Vanity]
      },
      {
        artifactKeyName: 'software', displayName: 'VR Bundler Unity Project', callerReferenceKey: 'software\\vrbundlerunityproject', isCutOffDate: false,
        products: [ProductName.VR]
      },
      {
        artifactKeyName: 'vanityconfig', displayName: 'Vanity Configs', callerReferenceKey: '', isCutOffDate: true,
        products: [ProductName.Vanity]
      },
    ];
  }

  get productNameConfiguration(): IProductNameConfiguration {
    return this.productNameConfigurationSubject.getValue();
  }

  get productNameConfigurations(): IProductNameConfiguration[] {
    return this.configurations ?? [];
  }

  set productNameConfiguration(val: IProductNameConfiguration) {
    this.productNameConfigurationSubject.next(val);
  }

  public getProductNameConfiguration(productName: ProductName | undefined): IProductNameConfiguration | undefined {
    return this.getAllConfigurations().find(x => x.productName === productName);
  }

  public getProductDescription(productName: ProductName | undefined): string {
    let conf = this.configurations?.find(x => x.productName === productName);

    return conf?.product ?? "";
  }

  public getAllConfigurations() {
    return [...this.configurations ?? [], this.getInsightsConfiguration(), this.getVanityConfiguration(), this.getUnrealConfiguration(), this.getConnectConfiguration()];
  }

  public getInsightsConfiguration() {
    return { productName: ProductName.Insights, product: "Insights", productColor: "rgba(96, 85, 85, 1)" } as IProductNameConfiguration;
  }

  public getVanityConfiguration() {
    return { productName: ProductName.Vanity, product: "situPARAMETRIC Vanity", productColor: "rgba(212, 89, 217, 1)" } as IProductNameConfiguration;
  }

  public getIfcPreviewConfiguration() {
    return { productName: ProductName.IfcPreview, product: "IFC Preview", productColor: "rgba(200, 124, 207, 1)" } as IProductNameConfiguration;
  }
  
  public getAssetPreviewConfiguration() {
    return { productName: ProductName.ProductPreview, product: "Asset Preview", productColor: "rgba(61, 174, 198, 1)" } as IProductNameConfiguration;
  }

  public getUnrealConfiguration() {
    return { productName: ProductName.Unreal, product: "situHOME", productColor: "rgb(36, 33, 33)" } as IProductNameConfiguration;
  }
  
  public getConnectConfiguration() {
    return { productName: ProductName.Connect, product: "situCONNECT", productColor: "rgb(25, 141, 60)" } as IProductNameConfiguration;
  }
}

export interface IProductNameConfiguration {
  productName: ProductName;
  product: string;
  productColor: string;
}

export interface IBaseIngredient {
  artifactKeyName: string;
  displayName: string;
  callerReferenceKey: string;
  isCutOffDate: boolean;
  products: ProductName[];
}